var DataTransformer           = require("./DataTransformer"),
    DeleteBookmarkFailMessage = require("app/Bookmarks/DeleteBookmarkFailMessage"),
    PageController            = require("app/core/PageController"),
    PageView                  = require("./PageView"),
    deepEqual                 = require("fast-deep-equal"),
    get                       = require("object-path").get,
    kind                      = require("enyo/kind"),
    logger                    = require("enyo/logger"),
    quickMacroize             = require("enyo/macroize").quickMacroize;

module.exports = kind(/** @lends  app.BookmarkPage.PageController.prototype */ {

    /**
     * @private
     */
    name: "app.BookmarkPage.PageController",

    /**
     * @private
     */
    kind: PageController,


    /**
     * Implement the logic for loading and displaying the page here.
     *
     * @param  {Object}    activation           The activation object
     * @param  {Object}    activation.params    Parameters to tell us what to do
     * @param  {boolean}   activation.finished  Flag if activation is finished
     * @param  {function}  activation.resolve   A promise resolver to fire when
     *                                          the page is active.
     * @param  {function}  activation.reject    A promise rejector to fire when
     *                                          the page failed to activate.
     * @public
     */
    execute: function(activation) {
        var app              = this.app,
            $appView         = app.view,
            $router          = app.$.router,
            $bookmarkService = app.$.bookmarkService,
            defaultLanguage  = app.$.i18nService.getDefaultLanguage(),
            language         = get(activation, "params.language", null) || defaultLanguage,
            skipCache        = get(activation, "params.skipCache", false);

        // Loading graphic
        $appView.requestShowLoadingGraphic();

        $bookmarkService.getBookmarks(language, skipCache)
            .then(this.bindSafely(function(bookmarksResponse) {
                var bookmarksData;

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                // Data was loaded successfully
                if (bookmarksResponse.status === "ok") {
                    bookmarksData = DataTransformer.transformBookmarks(language, bookmarksResponse.data);
                    this.updateState(activation.params, bookmarksData);
                    this.updateView(PageView);
                    this.updateDocumentTitle();

                    // Now that we are committed to displaying the current set
                    // of bookmarks, we need to respond to possible future
                    // changes to the bookmark collection.
                    this.watchBookmarks();
                }

                // Data not found
                else {
                    this.updateState(activation.params, []);

                    switch (bookmarksResponse.type) {
                        case "not_found_error":
                        case "server_error":
                        case "server_timeout_error":
                        case "bad_request_error":
                        default:
                            activation.resolve(1, function() {
                                $router.redirect(quickMacroize("/{$language}/oops", {
                                    language: language
                                }));
                            });

                            return;
                    }
                }
                activation.resolve(1);
            }))

            // Oops
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                activation.resolve(1, function() {
                    $router.redirect(quickMacroize("/{$language}/oops", {
                        language: language
                    }));
                });
            }))

            // No more loading graphic
            .finally(function() {
                $appView.requestHideLoadingGraphic();
            });
    },

    // Watch Bookmarks
    // --------------------------------

    /**
     * @type {boolean}
     * @private
     */
    isWatchingBookmarks: false,

    /**
     * Watch for bookmark updates.
     *
     * @private
     */
    watchBookmarks: function() {
        var $bookmarkService = this.app.$.bookmarkService;

        if (!this.isWatchingBookmarks) {
            $bookmarkService.on("BookmarkService::updated", this.onBookmarksUpdated, this);
            this.isWatchingBookmarks = true;
        }
    },

    /**
     * The bookmarks collection got updated, we need to update the page.
     */
    onBookmarksUpdated: function() {
        this.updateState(this.state.params, this.app.$.bookmarkService.get("bookmarks") || []);
    },

    /**
     * Stop watching for bookmark updates.
     *
     * @private
     */
    stopWatchingBookmarks: function() {
        var $bookmarkService = this.app.$.bookmarkService;

        if (this.isWatchingBookmarks) {
            $bookmarkService.off("BookmarkService::updated", this.onBookmarksUpdated, this);
            this.isWatchingBookmarks = false;
        }
    },

    // Delete Bookmarks
    // --------------------------------

    /**
     * Delete the bookmark.
     *
     * @param  {enyo.Control}  sender  The sender
     * @param  {Object}        event   The event
     * @private
     */
    onRequestDeleteBookmark: function(sender, event) {
        var appView  = this.app.view;

        appView.requestShowLoadingGraphic();
        this.app.$.bookmarkService
            .removeBookmark(event.bookmark)
            .then(this.bindSafely(function(response) {
                if (response.status !== "ok") {
                    this.onDeleteBookmarkFailed();
                }
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.onDeleteBookmarkFailed();
            }))
            .finally(this.bindSafely(function() {
                appView.requestHideLoadingGraphic();
            }));
    },

    /**
     * @private
     */
    onDeleteBookmarkFailed: function() {
        var message;

        // Display success message
        message = this.createComponent({
            kind: DeleteBookmarkFailMessage
        });
        message.render();
        message.requestShow(true, false);
        message.on("hidden", function() {
            message.destroy();
        });
    },

    // Page State
    // --------------------------------

    /**
     * @param  {Object}   params         The params passed to this controller
     * @param  {Object=}  bookmarksData  Freshly loaded bookmarks
     * @private
     */
    updateState: function(params, bookmarksData) {
        var state = {};

        // Build state
        state.params    = params;
        state.bookmarks = bookmarksData || [];

        // Update state only if it actually changed
        if (!deepEqual(this.state, state)) {
            this.set("state", state);
        }

        // Broadcast state changes to app state
        this.app.$.state.set("pageMediaType", null);
        this.app.$.state.set("pageMediaSlug", null);
        this.app.$.state.set("pageMediaId", null);
    },

    /**
     * Update the document title.
     *
     * @private
     */
    updateDocumentTitle: function() {
        var i18nService = this.app.$.i18nService;

        if (this.app.get("documentTitleOwner") !== this) {
            return;
        }

        this.app.setDocumentTitle(i18nService.getString("app.BookmarkPage.Document.title"));
    },

    // Deactivation
    // --------------------------------

    /**
     * @public
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.stopWatchingBookmarks();
            sup.apply(this, arguments);
        };
    })

});
